import s from './styles.module.scss'
import { useEffect, useState } from 'react'

interface InputFieldDateProps {
    fieldName: string
    saveValue: (obj: { field: string; value: string }) => { success: boolean; error: string }
    populateValue?: string
    enhanced?: boolean
    required?: boolean
    showErrors?: boolean
}

export const InputFieldDate = (props: InputFieldDateProps) => {
    const { saveValue, fieldName, populateValue, enhanced = false, required = false, showErrors = false } = props
    const [day, setDay] = useState<string>('')
    const [month, setMonth] = useState<string>('')
    const [year, setYear] = useState<string>('')
    const [error, setError] = useState('')

    // Generate arrays for days, months, and years
    const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'))
    const months = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
    ]

    // Calculate year range (90 years ago to 18 years ago)
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 90
    const maxYear = currentYear - 18
    const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => String(maxYear - i))

    useEffect(() => {
        if (populateValue) {
            const parts = populateValue.split('/')
            if (parts.length === 3) {
                setDay(parts[0])
                setMonth(parts[1])
                setYear(parts[2])
            }
        }
    }, [populateValue])

    useEffect(() => {
        if (showErrors && required && (!day || !month || !year)) {
            setError('This field is required')
        } else {
            setError('')
        }
    }, [showErrors, required, day, month, year])

    const updateDate = (newDay: string, newMonth: string, newYear: string) => {
        if (newDay && newMonth && newYear) {
            setError('')

            const formattedDate = `${newDay}/${newMonth}/${newYear}`
            const saveResult = saveValue({
                field: fieldName,
                value: formattedDate,
            })

            if (!saveResult.success) {
                setError(saveResult.error)
            }
        } else if (required) {
            if (newDay || newMonth || newYear) {
                setError('Please complete all date fields')
            }

            saveValue({
                field: fieldName,
                value: '',
            })
        }
    }

    const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newDay = e.target.value
        setDay(newDay)
        updateDate(newDay, month, year)
    }

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMonth = e.target.value
        setMonth(newMonth)
        updateDate(day, newMonth, year)
    }

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newYear = e.target.value
        setYear(newYear)
        updateDate(day, month, newYear)
    }

    return (
        <div className={`${s.datePickerContainer} ${enhanced ? s.enhanced : ''}`}>
            <select
                className={`${s.dateSelect} ${enhanced ? s.enhancedSelect : ''} ${error ? s.error : ''}`}
                value={day}
                onChange={handleDayChange}
                aria-label="Day"
            >
                <option value="">dd</option>
                {days.map((d) => (
                    <option key={d} value={d}>
                        {d}
                    </option>
                ))}
            </select>

            <select
                className={`${s.dateSelect} ${enhanced ? s.enhancedSelect : ''} ${error ? s.error : ''}`}
                value={month}
                onChange={handleMonthChange}
                aria-label="Month"
            >
                <option value="">mm</option>
                {months.map((m) => (
                    <option key={m.value} value={m.value}>
                        {m.label}
                    </option>
                ))}
            </select>

            <select
                className={`${s.dateSelect} ${enhanced ? s.enhancedSelect : ''} ${error ? s.error : ''}`}
                value={year}
                onChange={handleYearChange}
                aria-label="Year"
            >
                <option value="">yyyy</option>
                {years.map((y) => (
                    <option key={y} value={y}>
                        {y}
                    </option>
                ))}
            </select>

            {error && <span className={`${s.notice} ${error.length > 0 ? s.error : ''}`}>{error}</span>}
        </div>
    )
}
